<template>
  <div style="display: none">
    <div ref="content"><slot /></div>
  </div>
</template>

<script>
import { Popover } from "bootstrap";

export default {
  name: "BasePopover",
  inheritAttrs: false,
  props: {
    target: { type: String, required: true },
    placement: { type: String, default: "right" },
    customClass: { type: String, default: "" },
    html: { type: Boolean, default: true },
    container: { type: [String, Boolean], default: false },
    delay: { type: [Number, Object], default: 0 },
  },
  mounted() {
    const triggerEl = document.getElementById(this.target);
    if (!triggerEl) {
      throw new Error(`BasePopover: no element with id="${this.target}"`);
    }

    // Create the Popover
    this.popover = new Popover(triggerEl, {
      content: () => this.$refs.content.innerHTML,
      html: this.html,
      placement: this.placement,
      container: this.container,
      customClass: this.customClass,
      delay: this.delay,
      trigger: "manual",
      sanitize: false,
    });

    // Bind handlers so we can clean them up
    this._onShown = this._onPopoverShown.bind(this);
    this._onHidden = this._onPopoverHidden.bind(this);
    this._onToggleClick = (e) => {
      e.preventDefault();
      this.popover.toggle();
    };

    triggerEl.addEventListener("shown.bs.popover", this._onShown);
    triggerEl.addEventListener("hidden.bs.popover", this._onHidden);
    triggerEl.addEventListener("click", this._onToggleClick);

    // allow external .hide() via $emit("close")
    this.$on("close", () => this.popover.hide());
  },

  beforeDestroy() {
    const triggerEl = document.getElementById(this.target);
    if (triggerEl) {
      triggerEl.removeEventListener("shown.bs.popover", this._onShown);
      triggerEl.removeEventListener("hidden.bs.popover", this._onHidden);
      triggerEl.removeEventListener("click", this._onToggleClick);
    }
    if (this._onDocumentClick) {
      document.removeEventListener("mousedown", this._onDocumentClick);
      this._onDocumentClick = null;
    }
    this._removeBackdrop();
    if (this.popover) {
      this.popover.dispose();
      this.popover = null;
    }
  },

  methods: {
    _getTipElement() {
      return (
        (typeof this.popover.getTipElement === "function"
          ? this.popover.getTipElement()
          : this.popover.tip) || document.querySelector(".popover")
      );
    },

    _onPopoverShown() {
      const triggerEl = document.getElementById(this.target);
      const popEl = this._getTipElement();

      // delegate clicks INSIDE
      if (popEl) {
        this._insideClick = this._onPopoverClick.bind(this);
        popEl.addEventListener("click", this._insideClick);
      }

      // create the backdrop with the correct class
      const backdrop = document.createElement("div");
      backdrop.className = "base-popover-backdrop fade";
      document.body.appendChild(backdrop);

      // force reflow so fade→show transitions
      void backdrop.offsetHeight;
      backdrop.classList.add("show");
      this._backdropEl = backdrop;

      // hide on backdrop click
      backdrop.addEventListener("click", () => this.popover.hide());

      // outside‐click to close
      this._onDocumentClick = (e) => {
        if (!popEl.contains(e.target) && !triggerEl.contains(e.target)) {
          this.popover.hide();
        }
      };
      document.addEventListener("mousedown", this._onDocumentClick);
    },

    _onPopoverHidden() {
      const popEl = this._getTipElement();

      // remove inside‐popover click handler
      if (popEl && this._insideClick) {
        popEl.removeEventListener("click", this._insideClick);
        this._insideClick = null;
      }

      // remove click‐outside
      if (this._onDocumentClick) {
        document.removeEventListener("mousedown", this._onDocumentClick);
        this._onDocumentClick = null;
      }

      // fade out backdrop, then remove
      if (this._backdropEl) {
        this._backdropEl.classList.remove("show");
        // try the transitionend path
        this._backdropEl.addEventListener(
          "transitionend",
          () => this._removeBackdrop(),
          { once: true }
        );
        // a small timeout fallback
        setTimeout(() => this._removeBackdrop(), 300);
      }
    },

    _removeBackdrop() {
      if (this._backdropEl && this._backdropEl.parentNode) {
        this._backdropEl.parentNode.removeChild(this._backdropEl);
      }
      this._backdropEl = null;
    },

    _onPopoverClick(event) {
      if (event.target.closest(".close-button")) {
        this.popover.hide();
        this.$emit("close");
        return;
      }
      const msg = event.target.closest(".message-element");
      if (msg && msg.dataset.id) {
        this.popover.hide();
        this.$emit("select", msg.dataset.id);
        return;
      }
      const emo = event.target.closest(".emoji-element");
      if (emo && emo.dataset.id) {
        this.popover.hide();
        this.$emit("select", emo.dataset.id);
        return;
      }
    },
  },
};
</script>

<style>
.base-popover-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1060;
}
</style>
